/* eslint-disable no-param-reassign */
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import { GET_ALL_SWING_CONTRACTS } from '../../../constants/FieldNomsSwingContract';
import { post } from '../../../services/GenesisApi';
import {
  SwingContractData,
  SwingContractFilterValues,
} from '../../../types/FieldNomsSwingContract.type';
import {
  ActionableAlignStatusId,
  LatestAlignStatusId,
} from '../../../utils/fieldNomsSwingContract';
import DATEFORMAT from '../../../utils/dateFormat';
import { getCurrentGasDay } from '../../../utils/common/utils';

export interface IFieldNomsSwingContractState {
  uploadToAligne: {
    loading: boolean;
    uploadStatus: 'Success' | 'Failed' | null;
  };
  filters: SwingContractFilterValues & {
    pageNumber: number;
    pageSize: number;
    orderBy: string;
    sort: string;
    tableFilters: { fieldName: string; value: unknown }[];
  };
  loading: boolean;
  endurData: SwingContractData[];
  aligneData: SwingContractData[];
  totalEndurRecords: number;
  totalAligneRecords: number;
  lastRefreshed: string;
  error: string | null;
  activeTab: string;
}

const initialState: IFieldNomsSwingContractState = {
  uploadToAligne: {
    loading: false,
    uploadStatus: null,
  },
  filters: {
    filterType: 'WD',
    fromDate: getCurrentGasDay().format(DATEFORMAT.yearMonthDay),
    toDate: getCurrentGasDay().add(1, 'day').format(DATEFORMAT.yearMonthDay),
    energy: 'Therms',
    actionable: 'Yes',
    latest: 'No',
    pageNumber: 1,
    pageSize: 40,
    orderBy: '',
    sort: '',
    tableFilters: [],
  },
  loading: false,
  endurData: [],
  aligneData: [],
  totalEndurRecords: 0,
  totalAligneRecords: 0,
  lastRefreshed: '',
  error: null,
  activeTab: 'endur',
};

export const fetchSwingContractData = createAsyncThunk(
  'swingContract/fetchData',
  async (_, { getState, rejectWithValue }) => {
    const {
      FieldNomsSwingContract: { filters },
    } = getState() as { FieldNomsSwingContract: IFieldNomsSwingContractState };
    const {
      pageNumber,
      pageSize,
      orderBy,
      sort,
      tableFilters,
      filterType,
      fromDate,
      toDate,
      actionable,
      latest,
    } = filters;

    const payload = {
      pageNumber,
      pageSize,
      orderBy,
      sort,
      isShowLatestNonActionedRecord: actionable === 'Yes' || latest === 'Yes',
      alignStatusId: (() => {
        if (actionable === 'Yes') {
          return ActionableAlignStatusId;
        }
        if (latest === 'Yes') {
          return LatestAlignStatusId;
        }
        return undefined;
      })(),
    } as Record<string, unknown>;

    if (filterType === 'DR') {
      payload.fromDate = dayjs(fromDate)
        .startOf('day')
        .format(DATEFORMAT.yearMonthDayWithTimezoneHoursMinutesSeconds);
      payload.toDate = dayjs(toDate)
        .endOf('day')
        .format(DATEFORMAT.yearMonthDayWithTimezoneHoursMinutesSeconds);
    } else {
      payload.dataType = filterType;
    }

    tableFilters.forEach((filter) => {
      payload[filter.fieldName] = filter.value;
    });

    try {
      const response = await post(GET_ALL_SWING_CONTRACTS, payload);
      // Filter data by destination
      const allData = [
        ...(response?.data?.entryData ?? []),
        ...(response?.data?.exitData ?? []),
      ];
      const endurData = allData.filter(
        (item: SwingContractData) => item.destination === 'Endur'
      );
      const aligneData = allData.filter(
        (item: SwingContractData) => item.destination === 'Aligne'
      );
      return {
        endurData,
        aligneData,
        totalEndurRecords: endurData.length ?? 0,
        totalAligneRecords: aligneData.length ?? 0,
        refreshedTime: response?.data?.refreshedInfo?.refreshedTime,
      };
    } catch {
      return rejectWithValue('Failed to fetch swing contract data');
    }
  }
);

const swingContractSlice = createSlice({
  name: 'FieldNomsSwingContract',
  initialState,
  reducers: {
    clearUploadResponse: (state) => {
      state.uploadToAligne = {
        loading: false,
        uploadStatus: null,
      };
    },
    updateFilters(
      state,
      action: PayloadAction<Partial<IFieldNomsSwingContractState['filters']>>
    ) {
      state.filters = { ...state.filters, ...action.payload };
    },
    setActiveTab(state, action: PayloadAction<string>) {
      state.activeTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSwingContractData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSwingContractData.fulfilled, (state, action) => {
        state.loading = false;
        state.endurData = action.payload.endurData;
        state.aligneData = action.payload.aligneData;
        state.totalEndurRecords = action.payload.totalEndurRecords;
        state.totalAligneRecords = action.payload.totalAligneRecords;
        state.lastRefreshed = `Refreshed at ${dayjs(
          action.payload.refreshedTime
        ).format(DATEFORMAT.dayMonthDayWithHoursMinutesSecondsMeridiem)}`;
      })
      .addCase(fetchSwingContractData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearUploadResponse, updateFilters, setActiveTab } =
  swingContractSlice.actions;
export default swingContractSlice.reducer;
